/**
 * Config Guards for Insurance Connector
 *
 * Guards that check configuration flags and feature toggles
 */

import { parseDateFormat } from "@/components/InsuranceStepper/InsuranceStepper.helper";
import { differenceInYears } from "date-fns";
import type {
  InsuranceConnectorContext,
  InsuranceConnectorEvent,
} from "../types";

/**
 * Check if we should ask for the consumer's state
 * Based on the storefront_offboard_consumers config flag
 */
export const shouldAskState = ({
  context,
}: {
  context: InsuranceConnectorContext;
}) => {
  return context.storefrontOffboardConsumers === true;
};

/**
 * Check if storefront offboarding is disabled
 */
export const shouldNotAskState = ({
  context,
}: {
  context: InsuranceConnectorContext;
}) => {
  return context.storefrontOffboardConsumers !== true;
};

/**
 * Check if we have an organization ID
 */
export const hasOrgId = ({
  context,
}: { context: InsuranceConnectorContext }) => {
  return !!context.orgId && context.orgId > 0;
};

/**
 * Check if we have a organization name
 */
export const hasOrgName = ({
  context,
}: {
  context: InsuranceConnectorContext;
}) => {
  return !!context.orgName && context.orgName.length > 0;
};

/**
 * Check if we can proceed with unverified insurance
 * Based on the proceedWithUnverifiedInsurance config flag
 */
export const proceedWithUnverifiedInsuranceEnabled = ({
  context,
}: {
  context: InsuranceConnectorContext;
}) => {
  return context.proceedWithUnverifiedInsurance === true;
};

/**
 * Check if the user has primary insurance data in context
 */
export const hasPrimaryInsurance = ({
  context,
}: {
  context: InsuranceConnectorContext;
}) => {
  return !!context.primaryInsurance?.payer;
};

/**
 * Check if the disconnect target is secondary insurance
 */
export const isDisconnectingSecondary = ({
  context,
}: {
  context: InsuranceConnectorContext;
}) => {
  return context.disconnectTarget === "secondary";
};

/**
 * Check if the submitted birthday yields an age under the org's minimum age.
 */
export const isUnderMinimumAge = ({
  context,
  event,
}: {
  context: InsuranceConnectorContext;
  event: InsuranceConnectorEvent;
}) => {
  if (event.type !== "SUBMIT") return false;
  const birthday = (event.data as { birthday?: string })?.birthday;
  if (!birthday) return false;
  const minimumAge = context.minimumAge;
  if (!minimumAge || minimumAge <= 0) return false;
  const formattedDate = parseDateFormat(birthday);
  const age = differenceInYears(new Date(), new Date(formattedDate));
  return age < minimumAge;
};

/**
 * Check if the submitted state is not in the org's allowed states.
 */
export const isStateNotAllowed = ({
  context,
  event,
}: {
  context: InsuranceConnectorContext;
  event: InsuranceConnectorEvent;
}) => {
  if (event.type !== "SUBMIT") return false;
  const consumerState = (event.data as { consumerState?: string })
    ?.consumerState;
  if (!consumerState) return false;
  const { allowedStates } = context;
  if (!allowedStates || allowedStates.length === 0) return false;
  return !allowedStates.includes(
    consumerState.toUpperCase() as (typeof allowedStates)[number],
  );
};

/**
 * Ages that get the parent/guardian consent referral instead of a flat
 * age rejection. Deliberately absolute rather than `minimumAge - 2`: the
 * referral form asks for a legal guardian's consent, which is a statement
 * about these ages specifically, not about the org's booking threshold.
 */
const MINOR_CONSENT_REFERRAL_AGES = [15, 16, 17];

/**
 * The age shown on an under-age rejection: the youngest age that is not
 * flatly rejected. With the consent referral on, that is the youngest
 * referral age (15 to 17 are referred, not rejected), so a 14 year old at
 * an 18+ org is told 15, not 18. Without the referral it is the org's
 * minimum age, unchanged.
 */
export const getUnderAgeRejectionAge = (
  context: Pick<
    InsuranceConnectorContext,
    "minimumAge" | "minorConsentReferralEnabled"
  >,
) => {
  const { minimumAge } = context;
  if (!minimumAge || minimumAge <= 0) return minimumAge;
  if (!context.minorConsentReferralEnabled) return minimumAge;
  return Math.min(minimumAge, ...MINOR_CONSENT_REFERRAL_AGES);
};

/**
 * Shared predicate: is this birthday one we should route to the consent
 * referral rather than reject outright? Requires the org to have the
 * referral enabled and the patient to be under the org's minimum age, so
 * an org whose minimum age is already 16 never refers an eligible patient.
 */
const isMinorConsentAge = (
  birthday: string | undefined,
  context: InsuranceConnectorContext,
) => {
  if (!context.minorConsentReferralEnabled) return false;
  if (!birthday) return false;
  const minimumAge = context.minimumAge;
  if (!minimumAge || minimumAge <= 0) return false;
  const formattedDate = parseDateFormat(birthday);
  const age = differenceInYears(new Date(), new Date(formattedDate));
  if (age >= minimumAge) return false;
  return MINOR_CONSENT_REFERRAL_AGES.includes(age);
};

/**
 * Check the just-submitted birthday. Used on the birthday step to let a
 * 15, 16 or 17 year old past the age rejection so they can still be asked
 * their state before seeing the referral.
 */
export const isMinorConsentAgeFromEvent = ({
  context,
  event,
}: {
  context: InsuranceConnectorContext;
  event: InsuranceConnectorEvent;
}) => {
  if (event.type !== "SUBMIT") return false;
  const birthday = (event.data as { birthday?: string })?.birthday;
  return isMinorConsentAge(birthday, context);
};

/**
 * Check the birthday already saved in context. Used on the state step,
 * where the birthday came from an earlier step rather than this event.
 */
export const isMinorConsentAgeFromContext = ({
  context,
}: {
  context: InsuranceConnectorContext;
}) => isMinorConsentAge(context.birthday, context);
